import { ReactElement, useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Badge } from "@chakra-ui/react";
import { useVariantTunnel } from "./hooks/useVariantTunnel";
import { Calibration } from "./pages/calibration";
import { CalibrationV2 } from "./pages/calibrationV2";
import { Home } from "./pages/home";
import { SelectRecType } from "./pages/engines/[engineType]/select-rectype";
import { ProtectedRecording } from "./pages/engines/[engineType]/recording";
import { Result } from "./pages/engines/[engineType]/results/[fileId]";
import { Setting } from "./pages/setting";
import { QueryLoginResult } from "./pages/query-login-result";
import { AnalysisResult } from "./pages/analysis-result";
import { NotFound } from "./pages/404";
import { AnalysisError } from "./pages/engines/[engineType]/analysis-error";
import { useTranslation } from "react-i18next";
import { Engines } from "./pages/engines/";
import { EngineType } from "./pages/engines/[engineType]";
import { Login } from "./pages/login";
import { QueryLogin } from "./pages/query-login";
import { useAtom } from "jotai";
import { accessTokenInfoAtom, employeeInfoAtom } from "./store";
import { useQueryLoginRedirect } from "./hooks/useQueryLoginRedirect";
import { useDefaultPath } from "./hooks/useDefaultPath";
import dayjs from "dayjs";
import { PasswordReset } from "./pages/password-reset";
import { ResetId } from "./pages/password-reset/[resetId]";
import { AccountUnlocked } from "./pages/account-unlocked";
import { PasswordChange } from "./components/molecules/PasswordChange";
import { ColorLayout } from "./pages/color-layout";
import { SentryRoutes } from "../sentry";
import * as Sentry from "@sentry/react";
import { APP_TYPE } from "./environments";
import { HomePoC } from "./pages/home-poc";
import { checkWafResponse } from "./utils/checkWafResponse";
import { TwooCaCallback } from "./pages/callback/twooca-callback";

// ページ遷移時にスクロール位置をリセットする
function ScrollToTop(): ReactElement | null {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function ProtectedRoutes(): ReactElement {
  const { i18n } = useTranslation();
  const [employeeInfo] = useAtom(employeeInfoAtom);
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  let isValid;
  if (employeeInfo.employee_id) {
    const now = dayjs();
    isValid = tokenInfo && dayjs(tokenInfo.expired_at).isAfter(now);
    Sentry.setUser({ email: employeeInfo.employee_id });
  }

  return isValid ? (
    <Outlet />
  ) : (
    <Navigate to={`/${i18n.resolvedLanguage}/login`} replace />
  );
}

function AppRoot(): ReactElement {
  const { lang } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const variant = useVariantTunnel();

  useEffect(() => {
    // i18nに設定したサポートされている言語を取得
    const supportedLanguages = i18n.options.supportedLngs || [];

    if (lang !== undefined && supportedLanguages.includes(lang)) {
      // パスに設定されているlangがi18n設定と異なる場合はi18nの設定を変更
      if (i18n.resolvedLanguage !== lang) {
        i18n.changeLanguage(lang).then();
      }
    } else {
      // langが未サポートの言語の場合はログイン画面に飛ばす
      navigate(`/${i18n.resolvedLanguage}/login`, { replace: true });
    }
  }, [i18n, lang, navigate]);

  const location = useLocation();

  // WAFでメンテナンス時のアクセス制限(ステータスコード503)がかかっていたらリロードする
  // (WebACLSのカスタムレスポンスが表示される)
  useEffect(() => {
    const checkAndReload = async (): Promise<void> => {
      try {
        const status = await checkWafResponse(location.pathname);
        if (status === 503) {
          window.alert(t("Error.waf.maintenance"));
          window.location.reload();
        }
      } catch (error) {
        console.error("WAF確認エラー:", error);
      }
    };

    checkAndReload();
  }, [location.pathname, t]);

  return (
    <>
      {variant && (
        <Badge
          top={4}
          right={4}
          position="fixed"
          variant="solid"
          colorScheme="red"
          pointerEvents="none"
        >
          Variant: {variant}
        </Badge>
      )}
      <Outlet />
    </>
  );
}

function App(): ReactElement {
  const defaultPath = useDefaultPath();
  const rootRedirect = useQueryLoginRedirect({ defaultPath });

  return (
    <>
      <ScrollToTop />
      <SentryRoutes>
        <Route path="/" element={rootRedirect} />
        <Route path="twooca">
          <Route path="callback" element={<TwooCaCallback />} />
        </Route>
        <Route path=":lang" element={<AppRoot />}>
          <Route index element={<Navigate replace to={defaultPath} />} />
          {/* クエリログイン */}
          <Route path="query-login" element={<QueryLogin />} />
          <Route path="login" element={<Login />} />
          <Route path="account-unlocked" element={<AccountUnlocked />} />
          <Route path="password-reset">
            <Route index element={<PasswordReset />} />
            <Route path=":resetId" element={<ResetId />} />
          </Route>

          <Route element={<ProtectedRoutes />}>
            {APP_TYPE === "base" && <Route path="home" element={<Home />} />}
            {APP_TYPE === "poc" && <Route path="home" element={<HomePoC />} />}
            <Route path="calibration" element={<Calibration backButton />} />
            <Route
              path="calibration_new"
              element={<CalibrationV2 backButton />}
            />
            {/* 設定画面 */}
            <Route path="setting" element={<Setting />} />
            {/* パスワード設定画面 */}
            <Route path="setting/password" element={<PasswordChange />} />
            {/* カラー/レイアウト設定画面 */}
            <Route path="setting/color_layout" element={<ColorLayout />} />
            {/* クエリログイン時の音声録音解析完了画面 */}
            <Route path="query-login-result" element={<QueryLoginResult />} />
            {/* 解析結果グラフ画面 */}
            <Route path="analysis-result" element={<AnalysisResult />} />
            <Route path="engines">
              <Route index element={<Engines />} />
              <Route path=":engineType" element={<EngineType />}>
                <Route index element={<Navigate replace to="/404" />} />
                {/* 録音種別選択画面 */}
                <Route path="select-rectype" element={<SelectRecType />} />
                <Route path="recording" element={<ProtectedRecording />} />
                <Route path="analysis-error" element={<AnalysisError />} />
                <Route path="results">
                  <Route index element={<Navigate replace to="/404" />} />
                  <Route path=":fileId" element={<Result />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate replace to="/404" />} />
      </SentryRoutes>
    </>
  );
}

export default App;
